/* ==========================================================================
   LG Product Page — JS autonome (custom elements, aucune dépendance thème)
   - <lg-gallery>        : carrousel + vignettes + modale zoom
   - <lg-variant-picker> : sélection de variante, prix, dispo, URL
   - <lg-sticky-bar>     : barre d'achat qui apparaît au scroll
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- Galerie */
  class LGGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-lg-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-lg-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-lg-thumb]'));
      this.prev = this.querySelector('[data-lg-prev]');
      this.next = this.querySelector('[data-lg-next]');
      if (!this.viewport) return;

      this.thumbs.forEach((t) =>
        t.addEventListener('click', () => this.goTo(Number(t.dataset.lgThumb)))
      );
      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));

      this.viewport.addEventListener('scroll', this.debounce(() => this.sync(), 80));

      this.querySelectorAll('[data-lg-zoom]').forEach((btn) =>
        btn.addEventListener('click', () => this.openModal(Number(btn.dataset.lgZoom)))
      );
      this.modal = this.querySelector('[data-lg-modal]');
      if (this.modal) {
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal || e.target.closest('[data-lg-modal-close]')) this.closeModal();
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeModal();
        });
      }

      // Changement de média piloté par le sélecteur de variante
      this.addEventListener('lg:media', (e) => {
        const id = e.detail && e.detail.mediaId;
        if (!id) return;
        const idx = this.slides.findIndex((s) => s.dataset.lgMediaId === String(id));
        if (idx > -1) this.goTo(idx);
      });

      this.sync();
    }

    get index() {
      const w = this.viewport.clientWidth || 1;
      return Math.round(this.viewport.scrollLeft / w);
    }

    goTo(i) {
      const max = this.slides.length - 1;
      const idx = Math.max(0, Math.min(max, i));
      this.viewport.scrollTo({ left: idx * this.viewport.clientWidth, behavior: 'smooth' });
      this.setCurrent(idx);
    }

    step(dir) { this.goTo(this.index + dir); }

    sync() { this.setCurrent(this.index); }

    setCurrent(idx) {
      this.thumbs.forEach((t) =>
        t.setAttribute('aria-current', String(Number(t.dataset.lgThumb) === idx))
      );
      const active = this.thumbs[idx];
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      if (this.prev) this.prev.disabled = idx <= 0;
      if (this.next) this.next.disabled = idx >= this.slides.length - 1;
    }

    openModal(idx) {
      if (!this.modal) return;
      this.modal.dataset.open = 'true';
      document.documentElement.style.overflow = 'hidden';
      const target = this.modal.querySelector('[data-lg-modal-img="' + idx + '"]');
      if (target) target.scrollIntoView({ block: 'start' });
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.dataset.open = 'false';
      document.documentElement.style.overflow = '';
    }

    debounce(fn, wait) {
      let t;
      return function () {
        clearTimeout(t);
        t = setTimeout(fn, wait);
      };
    }
  }
  if (!customElements.get('lg-gallery')) customElements.define('lg-gallery', LGGallery);

  /* -------------------------------------------------------- Variant picker */
  class LGVariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.url = this.dataset.url;
      this.variants = this.parseVariants();

      this.addEventListener('change', () => this.onChange());
      this.querySelectorAll('[data-lg-swatch]').forEach((sw) => {
        sw.addEventListener('click', () => {
          const select = this.querySelector('select[data-lg-option-index="' + sw.dataset.lgOptionIndex + '"]');
          if (!select) return;
          select.value = sw.dataset.lgSwatch;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    parseVariants() {
      const el = this.querySelector('[data-lg-variants]');
      if (!el) return [];
      try { return JSON.parse(el.textContent); } catch (e) { return []; }
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('select[data-lg-option-index]')).map((s) => s.value);
    }

    get currentVariant() {
      const opts = this.selectedOptions;
      return this.variants.find((v) =>
        v.options.every((o, i) => o === opts[i])
      );
    }

    onChange() {
      const variant = this.currentVariant;
      this.updateSwatchState();
      this.updateOptionLabels();
      if (!variant) { this.setUnavailable(); return; }

      this.updateInputs(variant);
      this.updatePrice(variant);
      this.updateButtons(variant);
      this.updateUrl(variant);
      this.updateMedia(variant);
      document.dispatchEvent(new CustomEvent('lg:variant:change', { detail: { variant: variant, sectionId: this.sectionId } }));
    }

    updateSwatchState() {
      const value = this.querySelector('select[data-lg-option-index]');
      this.querySelectorAll('[data-lg-swatch]').forEach((sw) => {
        const select = this.querySelector('select[data-lg-option-index="' + sw.dataset.lgOptionIndex + '"]');
        sw.setAttribute('aria-current', String(!!select && select.value === sw.dataset.lgSwatch));
      });
      if (value) { /* noop, garde la référence lisible */ }
    }

    updateOptionLabels() {
      this.querySelectorAll('select[data-lg-option-index]').forEach((s) => {
        const out = this.querySelector('[data-lg-option-value="' + s.dataset.lgOptionIndex + '"]');
        if (out) out.textContent = s.value;
      });
    }

    updateInputs(variant) {
      document
        .querySelectorAll('input[data-lg-variant-id][form], input[data-lg-variant-id]')
        .forEach((input) => { input.value = variant.id; });
    }

    updatePrice(variant) {
      document.querySelectorAll('[data-lg-price-now]').forEach((el) => {
        el.textContent = this.money(variant.price);
      });
      document.querySelectorAll('[data-lg-price-was]').forEach((el) => {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          el.textContent = this.money(variant.compare_at_price);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });
      document.querySelectorAll('[data-lg-price-save]').forEach((el) => {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
          el.textContent = '-' + pct + '%';
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });
    }

    updateButtons(variant) {
      document.querySelectorAll('[data-lg-atc]').forEach((btn) => {
        btn.disabled = !variant.available;
        const label = btn.querySelector('[data-lg-atc-text]') || btn;
        if (!variant.available) {
          label.textContent = btn.dataset.lgSoldoutText || 'Épuisé';
        } else {
          label.textContent = btn.dataset.lgDefaultText || label.textContent;
        }
      });
      document.querySelectorAll('[data-lg-waitlist]').forEach((el) => { el.hidden = variant.available; });
    }

    setUnavailable() {
      document.querySelectorAll('[data-lg-atc]').forEach((btn) => {
        btn.disabled = true;
        const label = btn.querySelector('[data-lg-atc-text]') || btn;
        label.textContent = btn.dataset.lgUnavailableText || 'Indisponible';
      });
    }

    updateUrl(variant) {
      if (!this.url || !window.history.replaceState) return;
      window.history.replaceState({}, '', this.url + '?variant=' + variant.id);
    }

    updateMedia(variant) {
      if (!variant.featured_media_id) return;
      document.querySelectorAll('lg-gallery').forEach((g) => {
        g.dispatchEvent(new CustomEvent('lg:media', { detail: { mediaId: variant.featured_media_id } }));
      });
    }

    money(cents) {
      const fmt = this.dataset.moneyFormat || '{{amount}}';
      const amount = (cents / 100).toFixed(2);
      if (window.Shopify && window.Shopify.formatMoney) {
        try { return window.Shopify.formatMoney(cents, fmt); } catch (e) { /* fallback */ }
      }
      // Repli : format FR (1 234,56 €) si le format contient €, sinon $
      const parts = amount.split('.');
      const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return fmt.indexOf('€') > -1 || fmt.indexOf('EUR') > -1
        ? int + ',' + parts[1] + ' €'
        : '$' + int + '.' + parts[1];
    }
  }
  if (!customElements.get('lg-variant-picker')) customElements.define('lg-variant-picker', LGVariantPicker);

  /* ------------------------------------------------------------ Barre sticky */
  class LGStickyBar extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-lg-sticky]');
      this.watchSelector = this.dataset.watch || '[data-lg-buy-block]';
      const watched = document.querySelector(this.watchSelector);
      if (!this.bar || !watched) return;

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const past = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
            this.bar.dataset.visible = String(past);
          });
        },
        { threshold: 0, rootMargin: '0px' }
      );
      io.observe(watched);

      const proxy = this.querySelector('[data-lg-sticky-submit]');
      if (proxy) {
        proxy.addEventListener('click', () => {
          const form = document.querySelector(this.dataset.form || 'form[data-lg-product-form]');
          if (!form) return;
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        });
      }
    }
  }
  if (!customElements.get('lg-sticky-bar')) customElements.define('lg-sticky-bar', LGStickyBar);
})();
